import { Button, Dialog, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers-pro";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import type { VideoLibraryCategory, VideoLibraryItem } from "../../../api/types/videoLibraryTypes";
import MultiselectAutocomplete from "../../common/MultiselectAutocomplete";
import { getInitialEditVideoForm, mapFormToUpdateRequest, validateForm } from "./editVideoForm";

interface Props {
  videoInfo: VideoLibraryItem;
  allCategories: VideoLibraryCategory[];
  existingVideoNames?: string[];
  onClose: () => void;
  onUpdated: (updatedItem: VideoLibraryItem) => void;
}

const updateVideo = withErrorHandling(adminApi.updateVideoInLibrary);

const EditVideoDialog = ({ videoInfo, allCategories, existingVideoNames, onClose, onUpdated }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [form, setForm] = useState(() => getInitialEditVideoForm(videoInfo, allCategories));
  const [saving, setSaving] = useState(false);

  const handleSaveClick = async () => {
    const payload = mapFormToUpdateRequest(form);
    setSaving(true);
    const [updatedItem, error] = await updateVideo(videoInfo.id, payload);
    setSaving(false);

    if (error) {
      logError(error, "[EditVideoDialog] updateVideo");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Video updated successfully");
    onUpdated(updatedItem);
    onClose();
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, title: e.target.value });
  };

  const handleCategoryChange = (selected: VideoLibraryCategory[]) => {
    setForm({ ...form, categories: selected });
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, description: e.target.value });
  };

  const handleUploadedAtChange = (newValue: Date | null) => {
    setForm({ ...form, uploadedAt: newValue });
  };

  const { isFormValid, validationErrors } = validateForm(form, existingVideoNames ?? []);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Video Details</DialogTitle>
      <DialogCloseButton onClick={onClose} />

      <DialogContent>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Title"
            value={form.title}
            onChange={handleTitleChange}
            error={!!validationErrors.title}
            helperText={validationErrors.title}
          />

          <MultiselectAutocomplete
            options={allCategories}
            values={form.categories}
            onSelected={handleCategoryChange}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            placeholder="Select one or more categories"
            error={!!validationErrors.categories}
            helperText={validationErrors.categories}
          />

          <DateTimePicker
            label="Uploaded At"
            value={form.uploadedAt}
            onChange={handleUploadedAtChange}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />

          <TextField
            fullWidth
            multiline
            rows={5}
            label="Description (optional)"
            value={form.description}
            onChange={handleDescriptionChange}
          />
        </Stack>
      </DialogContent>

      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={saving} onClick={handleSaveClick} disabled={!isFormValid}>
          Save
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default EditVideoDialog;
